<template>
  <div
    v-if="condition?.outcome"
    class="flex cursor-default select-none items-center justify-between gap-x-4 p-3"
    :class="`${hoverClass} opacity-100`"
  >
    <div class="flex items-center gap-x-4">
      <slot name="handle"></slot>
      <div
        class="flex h-[56px] w-[56px] items-center justify-center rounded-full"
        :class="iconBgClass"
      >
        <slot></slot>
      </div>
      <div class="flex w-[32rem] flex-col">
        <div :class="`text-sm font-semibold leading-5 ${textClass}`">
          {{ showButtonText(condition) }}
        </div>
        <div
          v-for="(row, index) in conditionRows"
          :key="index"
          class="condition-summary-row text-xs leading-5 text-gray-600"
        >
          <span :class="`uppercase ${textClass} mr-1`">
            {{ row.logicLabel }}
          </span>
          <span v-if="row.fieldLabel" class="mr-1">
            {{ row.fieldLabel }}
          </span>
          <span v-if="row.categoryLabel && !row.categoryAfterOperation" class="mr-1">
            {{ row.categoryLabel }}
          </span>
          <span
            v-if="!row.isSlideCondition"
            :class="`uppercase ${textClass} mr-1`"
          >
            {{ row.operationLabel }}
          </span>
          <span v-if="row.categoryLabel && row.categoryAfterOperation" class="mr-1">
            {{ row.categoryLabel }}
          </span>
          <span v-if="row.shouldShowValue">
            <template v-if="row.isBetween">
              {{ row.betweenValues.start }}{{ row.inlineScoreFormatLabel }}
              <span :class="`uppercase ${textClass} mx-1`">{{
                t("form.conditonalLogic.logic.and")
              }}</span>
              {{ row.betweenValues.end }}{{ row.inlineScoreFormatLabel }}
            </template>
            <template v-else>
              {{ row.inputValue }}{{ row.inlineScoreFormatLabel }}
            </template>
          </span>
          <span v-if="row.scoreFormatLabel" class="condition-score-format ml-1">
            {{ row.scoreFormatLabel }}
          </span>
        </div>
        <div
          v-if="condition?.conditions.length > 5"
          class="text-xs leading-5 text-gray-600"
        >
          {{ t("form.conditonalLogic.additionalRulesPrefix") }}&nbsp;
          <span :class="textClass">{{ condition.conditions.length - 5 }}</span>
          &nbsp;
          {{
            t("form.conditonalLogic.additionalRulesSuffix", {
              ifLabel: t("form.conditonalLogic.logic.if").toUpperCase(),
            })
          }}
        </div>
        <div class="flex text-xs leading-5 text-gray-600">
          <span :class="`uppercase ${textClass} min-w-fit`">
            {{ outcomeText(condition) }}
            &nbsp;
          </span>
          <span v-html="showOutcomeValue(condition)"></span>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-x-4">
      <UIButton
        id="edit-condition"
        text
        size="small"
        class="text-gray-800"
        @click="handleEdit"
      >
        <Edit01Icon class="h-5 w-5" />
      </UIButton>
      <UIButton
        id="delete-condition"
        text
        size="small"
        class="text-gray-800"
        @click="handleDelete"
      >
        <Trash01Icon class="h-5 w-5" />
      </UIButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import {
  formatTemporalConditionValue,
  isTemporalConditionKind,
  parseProductConditionKey,
  resolveConditionalFieldKind,
  shouldHideInput,
  type FieldLike,
} from "@/util/conditional-logic-helpers";
import {
  colorBgHoverVariants,
  colorBgVariants,
  colorTextVariants,
} from "@/util/constants";
import {
  PAYMENT_SECTION_KEY,
  countProductTargetsFromKeys,
  splitShowHideValue,
} from "@/util/product-conditional-logic";
import {
  allOperationOptions,
  camelToPascal,
  dateFormatForDayjs,
} from "@/util/methods";
import { Edit01Icon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  color: {
    type: String,
    default: "gray",
  },
  buttonText: {
    type: String,
    default: () => {
      const { t: translate } = useI18n();
      return translate("form.conditonalLogic.showHideFields");
    },
  },
  outcomeType: {
    type: String,
    default: () => {
      const { t: translate } = useI18n();
      return translate("form.conditonalLogic.showHideFields");
    },
  },
  condition: {
    type: Object,
    default: null,
  },
});

const store = useAppStore();
const emit = defineEmits(["onEditButtonClick", "onDeleteButtonClick"]);

type ConditionSummaryItem = {
  selectedField?: string | null;
  selectedOperation?: string | null;
  fieldMode?: string;
  inputValue?: unknown;
  dateFormat?: string;
  categoryId?: string | null;
  scoreFormat?: string | null;
};

type ConditionField = FieldLike & {
  children?: ConditionField[];
  label?: unknown;
};

const iconBgClass = ref(colorBgVariants[props.color]);
const hoverClass = ref(colorBgHoverVariants[props.color]);
const textClass = ref(colorTextVariants[props.color]);

watchEffect(() => {
  iconBgClass.value = colorBgVariants[props.color];
  hoverClass.value = colorBgHoverVariants[props.color];
  textClass.value = colorTextVariants[props.color];
});

const truncate = (s: string, max = 32) =>
  s && s.length > max ? s.slice(0, max) + "…" : s;

const toPlain = (s: string) => (s || "").replace(/<\/?[^>]+(>|$)/g, "");

const flattenConditionFields = (
  fields?: ConditionField[]
): ConditionField[] => {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.flatMap((field) => [
    field,
    ...flattenConditionFields(field.children),
  ]);
};

const addFieldToLookup = (
  lookup: Map<string, ConditionField>,
  field: ConditionField | undefined
) => {
  if (!field) return;

  [field.hiddenFieldQueryKey, field.tag, field.id]
    .filter(Boolean)
    .forEach((key) => lookup.set(String(key), field));
};

const conditionFieldLookup = computed(() => {
  const lookup = new Map<string, ConditionField>();
  const slideFields = (store.slides || []).flatMap((slide) =>
    flattenConditionFields(slide?.slideData || [])
  );
  [
    ...flattenConditionFields(store.elements || []),
    ...flattenConditionFields(store.addressSettings?.children || []),
    ...slideFields,
  ].forEach((field) => addFieldToLookup(lookup, field));

  return lookup;
});

const getConditionField = (
  selectedField?: string | null
): ConditionField | undefined => {
  if (!selectedField) {
    return undefined;
  }

  return conditionFieldLookup.value.get(selectedField);
};

const formatRawInputValue = (value: unknown): string => {
  return typeof value === "string" ? value : value != null ? String(value) : "";
};

const formatConditionInputValue = (
  conditionItem: ConditionSummaryItem,
  value: unknown = conditionItem?.inputValue
): string => {
  const field = getConditionField(conditionItem?.selectedField);
  const fieldType = resolveConditionalFieldKind(field);

  if (isTemporalConditionKind(fieldType)) {
    return formatTemporalConditionValue(
      value as string | number | null | undefined,
      fieldType,
      dateFormatForDayjs(field?.format, field?.separator),
      field?.timeFormat,
      conditionItem?.dateFormat
    );
  }

  return formatRawInputValue(value);
};

const getHideTypeLabel = (hideType: string | undefined): string => {
  switch (hideType) {
    case "Hide Multiple":
      return t("form.conditonalLogic.hideFields");
    case "Show Multiple":
      return t("form.conditonalLogic.showFields");
    case "Hide":
      return t("form.conditonalLogic.hideField");
    case "Show":
      return t("form.conditonalLogic.showField");
    default:
      return "";
  }
};

const getOperationLabel = (operation: string | null | undefined): string => {
  if (!operation) return "";
  return (
    allOperationOptions.find((option) => option.value === operation)?.label ||
    camelToPascal(operation)
  );
};

const getQuizScoreFieldLabel = (
  selectedField: string | null | undefined
): string => {
  if (!store.quizId) {
    return "";
  }

  switch (selectedField) {
    case "highestScore":
      return t("form.conditonalLogic.highestScore");
    case "lowestScore":
      return t("form.conditonalLogic.lowestScore");
    case "overAllScore":
      return t("form.conditonalLogic.overallScore");
    case "categoryScore":
      return t("form.conditonalLogic.categoryScore");
    default:
      return "";
  }
};

const getQuizCategoryLabel = (categoryId: string | null | undefined): string =>
  categoryId
    ? truncate(
        toPlain(
          String(
            store.category?.find((category) => category.id === categoryId)
              ?.label || ""
          )
        )
      )
    : "";

const isRankedCategoryField = (selectedField: string | null | undefined): boolean =>
  selectedField === "highestScore" || selectedField === "lowestScore";

const getQuizScoreFormatLabel = (
  scoreFormat: string | null | undefined
): string => {
  switch (scoreFormat || "percentage") {
    case "percentage":
    case "actual-score":
      return "";
    case "out-of-10":
      return `(${t("form.conditonalLogic.outOf10")})`;
    default:
      return `(${camelToPascal(scoreFormat || "")})`;
  }
};

/**
 * Resolve a product/bump target key to a display label using the catalog details
 * loaded into the store. Returns null when the key is not a product key, and a
 * "deleted" label when the product/variant no longer exists.
 */
const resolveProductConditionLabel = (key: string): string | null => {
  const ref = parseProductConditionKey(key);
  if (!ref) {
    return null;
  }

  if (ref.kind === "bumpProduct") {
    const bump = ((store.bumProductList as any[]) || []).find(
      (b) => b.productId === ref.productId && b.priceId === ref.priceId
    );
    const name = bump?.name || t("form.conditonalLogic.deletedProduct");
    return truncate(`${name} (${t("form.conditonalLogic.bumpSuffix")})`);
  }

  const product = ((store.addedProductList as any[]) || []).find(
    (p) => p.productId === ref.productId
  );
  if (!product) {
    return truncate(t("form.conditonalLogic.deletedProduct"));
  }
  if (ref.priceId === null) {
    return truncate(`${product.name} (${t("form.conditonalLogic.anyVariant")})`);
  }
  const price = (product.prices || []).find((p: any) => p._id === ref.priceId);
  const variantName = price?.name || t("form.conditonalLogic.deletedVariant");
  return truncate(`${product.name} (${variantName})`);
};

const getFieldName = (hiddenFieldQueryKey) => {
  if (!hiddenFieldQueryKey) {
    return "";
  }

  if (store.quizId) {
    const quizScoreFieldLabel = getQuizScoreFieldLabel(hiddenFieldQueryKey);
    if (quizScoreFieldLabel) {
      return quizScoreFieldLabel;
    }

    const quizFieldLabel = truncate(
      toPlain(String(getConditionField(hiddenFieldQueryKey)?.label || ""))
    );
    if (quizFieldLabel) {
      return quizFieldLabel;
    }
  }

  const productLabel = resolveProductConditionLabel(String(hiddenFieldQueryKey));
  if (productLabel !== null) {
    return productLabel;
  }

  // Handle split Terms & Conditions keys like "terms_and_conditions_1" / "terms_and_conditions_2"
  const tncMatch = String(hiddenFieldQueryKey).match(/^(.*)_(1|2)$/);
  const tncBaseKey = tncMatch ? tncMatch[1] : null;
  const tncVariantIndex = tncMatch ? tncMatch[2] : null;

  // For forms
  if (store.formId) {
    const directRaw =
      store.elements.find(
        (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
      )?.label ||
      store.addressSettings.children.find(
        (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
      )?.label;
    const direct = truncate(toPlain(String(directRaw || "")));
    if (direct) {
      return direct;
    }
    if (tncBaseKey) {
      const baseEl =
        store.elements.find((e) => e.hiddenFieldQueryKey === tncBaseKey) ||
        store.addressSettings.children.find(
          (e) => e.hiddenFieldQueryKey === tncBaseKey
        );
      if (baseEl && baseEl.type === "terms_and_conditions") {
        return tncVariantIndex === "1" ? t("common.tnc1") : t("common.tnc2");
      }
    }
    return "";
  }

  // For surveys
  if (store.surveyId) {
    for (const slide of store.slides) {
      if (slide.slideData && Array.isArray(slide.slideData)) {
        const field = slide.slideData.find(
          (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
        );
        if (field) {
          return truncate(toPlain(String(field.label || "")));
        }
        if (tncBaseKey) {
          const baseField = slide.slideData.find(
            (element) => element.hiddenFieldQueryKey === tncBaseKey
          );
          if (baseField && baseField.type === "terms_and_conditions") {
            return tncVariantIndex === "1"
              ? t("common.tnc1")
              : t("common.tnc2");
          }
        }
      }
    }
  }

  return (
    truncate(
      toPlain(
        String(
          store.elements.find(
            (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
          )?.label ||
            store.addressSettings?.children?.find(
              (element) => element.hiddenFieldQueryKey === hiddenFieldQueryKey
            )?.label ||
            ""
        )
      )
    ) ||
    (tncBaseKey &&
    (store.elements.find((e) => e.hiddenFieldQueryKey === tncBaseKey)?.type ===
      "terms_and_conditions" ||
      store.addressSettings?.children?.find(
        (e) => e.hiddenFieldQueryKey === tncBaseKey
      )?.type === "terms_and_conditions")
      ? tncVariantIndex === "1"
        ? t("common.tnc1")
        : t("common.tnc2")
      : "")
  );
};

const handleEdit = () => emit("onEditButtonClick");

const handleDelete = () => emit("onDeleteButtonClick");

const outcomeText = (condition) => {
  const outcome = condition?.outcome;
  if (!outcome) {
    return "";
  }
  if (
    outcome.type === "redirectToUrl" ||
    (outcome.type === "disqualifyLead" &&
      outcome.disqualifyAction === "openUrl")
  ) {
    return t("common.openUrl");
  }
  if (
    outcome.type === "displayCustomMessage" ||
    (outcome.type === "disqualifyLead" &&
      outcome.disqualifyAction === "showCustomMessage")
  ) {
    return t("form.conditonalLogic.outcomeLabels.show").toUpperCase();
  }
  if (outcome.type === "showHideFields") {
    return getHideTypeLabel(outcome.hideType);
  }
  if (outcome.type === "jumpTo") {
    return t("form.conditonalLogic.outcomeLabels.jumpTo").toUpperCase();
  }
  return "";
};

/** Label of the payment element itself, used for whole-section and product summaries. */
const paymentElementLabel = computed(() => {
  const element = (store.elements || []).find(
    (item: any) => item?.hiddenFieldQueryKey === PAYMENT_SECTION_KEY
  );
  return toPlain(String(element?.label || "")) || t("fields.integration.sellProducts");
});

/**
 * Collapse a rule's product targets into one "<section> - N products" entry, so a rule
 * hiding a dozen variants still reads as a single line. "Entire section" keeps the plain
 * element label, matching what the Show/Hide dropdown shows.
 */
const summarizeShowHideTargets = (value: string[]): string[] => {
  const { fieldKeys, productKeys, hasEntireSection } = splitShowHideValue(value);
  const labels = fieldKeys.map((queryKey) => getFieldName(queryKey)).filter(Boolean);

  if (hasEntireSection) {
    labels.push(paymentElementLabel.value);
  } else if (productKeys.length > 0) {
    labels.push(
      `${paymentElementLabel.value} \u00b7 ${t("form.conditonalLogic.productsCountShort", {
        count: countProductTargetsFromKeys(productKeys),
      })}`
    );
  }

  return labels;
};

const showOutcomeValue = (condition) => {
  const { outcome } = condition;
  // Both singular and "Multiple" hideTypes are summarized the same way: a singular rule
  // can still carry several product keys under one Sell Products target.
  if (outcome.type === "showHideFields") {
    const fieldLabels = summarizeShowHideTargets(outcome.value);

    if (fieldLabels.length > 5) {
      const shown = fieldLabels.slice(0, 5).join(", ");
      const remaining = fieldLabels.length - 5;
      return t("form.conditonalLogic.shownTargetsWithMore", {
        shown,
        count: remaining,
      });
    }
    return fieldLabels.join(", ");
  }

  if (outcome.type === "jumpTo" && outcome.jumpTarget) {
    if (outcome.jumpTarget === "submitSurvey") {
      return t("form.conditonalLogic.submitSurvey");
    }
    const slideIndex = store.slides.findIndex(
      (slide) => slide.id === outcome.jumpTarget
    );
    if (slideIndex !== -1) {
      return t("form.conditonalLogic.pageTitle", {
        index: slideIndex + 1,
        title: store.slides[slideIndex].slideName,
      });
    }
    return t("form.conditonalLogic.selectedSlide");
  }

  return getFieldName(outcome.value) || outcome.value;
};

const showButtonText = (condition) => {
  const outcome = condition?.outcome;
  if (outcome?.type === "showHideFields") {
    const hideTypeLabel = getHideTypeLabel(outcome?.hideType);
    return hideTypeLabel || "";
  } else if (outcome?.type === "jumpTo") {
    return t("form.conditonalLogic.jumpToSlide");
  }
  return props.buttonText;
};

// Plain value formatters (no HTML)
const getBetweenValues = (
  conditionItem: ConditionSummaryItem
): { start: string; end: string } => {
  let payload: unknown = conditionItem?.inputValue;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return { start: String(conditionItem?.inputValue ?? ""), end: "" };
    }
  }
  const payloadRecord =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  const start = payloadRecord.start ?? "";
  const end = payloadRecord.end ?? "";
  return {
    start: formatConditionInputValue(conditionItem, start),
    end: formatConditionInputValue(conditionItem, end),
  };
};

const formatPlainInputValue = (conditionItem: ConditionSummaryItem): string =>
  formatConditionInputValue(conditionItem);

const getLogicLabel = (
  index: number,
  conditionalOperation?: string
): string => {
  if (index === 0) return t("form.conditonalLogic.logic.if");

  switch ((conditionalOperation || "").toLowerCase()) {
    case "and":
      return t("form.conditonalLogic.logic.and");
    case "or":
      return t("form.conditonalLogic.logic.or");
    case "then":
      return t("form.conditonalLogic.logic.then");
    default:
      return "";
  }
};

const conditionRows = computed(() =>
  (props.condition?.conditions || [])
    .slice(0, 5)
    .map((conditionItem: ConditionSummaryItem, index: number) => {
      const isSlide = isSlideCondition(conditionItem);
      const quizScoreFieldLabel = getQuizScoreFieldLabel(
        conditionItem.selectedField
      );
      const rankedCategoryField = isRankedCategoryField(conditionItem.selectedField);
      const isBetween = conditionItem.selectedOperation === "between";
      const shouldShowValue =
        !isSlide &&
        !rankedCategoryField &&
        !shouldHideInput(conditionItem.selectedOperation || "");

      return {
        logicLabel: getLogicLabel(index, props.condition?.conditionalOperation),
        fieldLabel: isSlide
          ? formatSlideLabel(conditionItem.selectedField || "")
          : getFieldName(conditionItem.selectedField),
        categoryLabel:
          conditionItem.selectedField === "categoryScore" || rankedCategoryField
            ? getQuizCategoryLabel(conditionItem.categoryId)
            : "",
        categoryAfterOperation: rankedCategoryField,
        isSlideCondition: isSlide,
        operationLabel: isSlide
          ? ""
          : getOperationLabel(conditionItem.selectedOperation),
        scoreFormatLabel:
          quizScoreFieldLabel && shouldShowValue
            ? getQuizScoreFormatLabel(conditionItem.scoreFormat)
            : "",
        inlineScoreFormatLabel:
          quizScoreFieldLabel &&
          shouldShowValue &&
          (!conditionItem.scoreFormat ||
            conditionItem.scoreFormat === "percentage")
            ? "%"
            : "",
        shouldShowValue,
        isBetween,
        betweenValues: isBetween
          ? getBetweenValues(conditionItem)
          : { start: "", end: "" },
        inputValue: isBetween ? "" : formatPlainInputValue(conditionItem),
      };
    })
);

// Identify slide-based IF rows (Jump To slide mode)
const isSlideCondition = (
  conditionItem:
    | { selectedField?: string; fieldMode?: string }
    | null
    | undefined
): boolean => {
  if (!conditionItem) {
    return false;
  }
  if (conditionItem?.fieldMode === "slide") {
    return true;
  }
  if (!store.surveyId) {
    return false;
  }
  return store.slides.some((s) => s.id === conditionItem?.selectedField);
};

const formatSlideLabel = (id: string) => {
  if (!id || !store.surveyId) {
    return "";
  }
  if (id === "submitSurvey") {
    return t("form.conditonalLogic.submitSurvey");
  }
  const index = store.slides.findIndex((s) => s.id === id);
  if (index === -1) {
    return "";
  }
  return t("form.conditonalLogic.pageTitle", {
    index: index + 1,
    title: store.slides[index].slideName,
  });
};
</script>
